import type { CurrencyCode } from './common';

/**
 * Tax regime — the statutory tax model a tenant operates under, derived purely
 * from its (immutable) `default_currency`. See `docs/multi-currency-plan.md` §7.
 *
 * - `GST_INDIA`     — India: per-slab GST (0/5/12/18/28), CGST/SGST or IGST,
 *                      GSTIN, GSTR1/Tally/HSN. Behaviour unchanged.
 * - `VAT_UAE`       — UAE: single VAT line, 5%, TRN.
 * - `VAT_KSA`       — Saudi Arabia: single VAT line, 15%, TRN/VAT no.
 *                      (ZATCA/Fatoora e-invoicing is OUT OF SCOPE.)
 * - `VAT_OMAN`      — Oman: single VAT line, 5%, VATIN.
 * - `VAT_BAHRAIN`   — Bahrain: single VAT line, 10%, VAT no.
 * - `NO_VAT`        — Kuwait & Qatar: no VAT regime, 0%.
 *
 * NOTE: This type and the helpers below are pure config only. Nothing consumes
 * the regime yet — task 240 introduces behavioural gating. INR behaviour is and
 * must remain byte-identical.
 */
export type TaxRegime =
    | 'GST_INDIA'
    | 'VAT_UAE'
    | 'VAT_KSA'
    | 'VAT_OMAN'
    | 'VAT_BAHRAIN'
    | 'NO_VAT';

/**
 * Currency → tax regime mapping (single source of truth, frozen).
 * Per `docs/multi-currency-plan.md` §7:
 *   INR → GST_INDIA, AED → VAT_UAE, SAR → VAT_KSA, OMR → VAT_OMAN,
 *   BHD → VAT_BAHRAIN, KWD → NO_VAT, QAR → NO_VAT.
 */
const CURRENCY_TAX_REGIME: Readonly<Record<CurrencyCode, TaxRegime>> = Object.freeze({
    INR: 'GST_INDIA',
    AED: 'VAT_UAE',
    SAR: 'VAT_KSA',
    QAR: 'NO_VAT',
    OMR: 'VAT_OMAN',
    BHD: 'VAT_BAHRAIN',
    KWD: 'NO_VAT',
});

/**
 * The country a tenant operates in. There is no `country` column — the tenant's
 * immutable `default_currency` is the single source of truth for the statutory
 * country (see the launch-set table in `docs/multi-currency-plan.md` §2), and
 * that country is what drives the tax regime, invoice format and bill format.
 */
export interface TenantCountry {
    /** ISO 3166-1 alpha-2 code, e.g. `IN`. */
    readonly iso: string;
    /** Display name, matching the launch-set table. */
    readonly name: string;
    /** E.164 dial prefix, including the leading `+`. */
    readonly dial: string;
    /**
     * How many digits a subscriber number has, without the dial prefix.
     *
     * Needed to tell a national number from a country code someone typed
     * without a `+`. A storefront that guesses gets this catastrophically
     * wrong: the Indian mobile `9187654321` starts with `91`, and reading that
     * as the country code turns a real number into `+9187654321`. Comparing
     * the total length against `dial.length - 1 + nationalDigits` is exact.
     */
    readonly nationalDigits: readonly number[];
}

/**
 * Currency → country mapping (single source of truth, frozen). Mirrors
 * {@link CURRENCY_TAX_REGIME} one-for-one: INR → India, AED → UAE, SAR → Saudi
 * Arabia, QAR → Qatar, OMR → Oman, BHD → Bahrain, KWD → Kuwait.
 */
const CURRENCY_COUNTRY: Readonly<Record<CurrencyCode, TenantCountry>> = Object.freeze({
    INR: { iso: 'IN', name: 'India', dial: '+91', nationalDigits: [10] },
    AED: { iso: 'AE', name: 'United Arab Emirates', dial: '+971', nationalDigits: [9] },
    SAR: { iso: 'SA', name: 'Saudi Arabia', dial: '+966', nationalDigits: [9] },
    QAR: { iso: 'QA', name: 'Qatar', dial: '+974', nationalDigits: [8] },
    OMR: { iso: 'OM', name: 'Oman', dial: '+968', nationalDigits: [8] },
    BHD: { iso: 'BH', name: 'Bahrain', dial: '+973', nationalDigits: [8] },
    KWD: { iso: 'KW', name: 'Kuwait', dial: '+965', nationalDigits: [8] },
});

/**
 * Derive the country of operation for a tenant currency. Pure — no I/O, total
 * over the in-scope {@link CurrencyCode} set.
 */
export function getCountryForCurrency(currency: CurrencyCode): TenantCountry {
    return CURRENCY_COUNTRY[currency];
}

/**
 * Sentinel for `GST_INDIA` in {@link REGIME_STANDARD_RATE}: India tax is applied
 * per GST slab (0/5/12/18/28), so there is no single flat "standard rate". This
 * value MUST NOT be used as a flat percentage — callers gate on
 * `regime === 'GST_INDIA'` and take the per-slab path instead (task 240).
 */
export const GST_INDIA_PER_SLAB = -1 as const;

/**
 * Tax regime → standard tax rate (percentage points), frozen.
 *
 * GCC VAT regimes carry their country standard rate; `NO_VAT` is 0.
 * `GST_INDIA` is per-slab and therefore has no flat rate — its entry is the
 * documented {@link GST_INDIA_PER_SLAB} sentinel, never a usable percentage.
 *
 * Typed `Record<TaxRegime, number>` (see task scope); the sentinel keeps the
 * map total without widening the value type.
 */
export const REGIME_STANDARD_RATE: Readonly<Record<TaxRegime, number>> = Object.freeze({
    GST_INDIA: GST_INDIA_PER_SLAB,
    VAT_UAE: 5,
    VAT_KSA: 15,
    VAT_OMAN: 5,
    VAT_BAHRAIN: 10,
    NO_VAT: 0,
});

/**
 * Statutory tax-identifier format of a regime — the single source of truth for
 * the tenant-level tax id (GSTIN / TRN / VATIN / VAT No.). Consumed by the
 * `@bbpos/ui` resolver (tenant wizard, settings, `GstinInput`) and by the API
 * DTO validator, so the on-screen hint and the server rule cannot drift.
 *
 * Patterns carry no `g`/`y` flag — they are shared singletons and `.test()`
 * must stay stateless. Labels live with the presentation layers.
 */
export interface TaxIdFormat {
    readonly pattern: RegExp;
    /** Exact character count — drives the input `maxLength`. */
    readonly length: number;
    readonly example: string;
    /** Human hint shown beside the field. */
    readonly hint: string;
}

/**
 * Regime → tax-id format (frozen). `NO_VAT` (Kuwait, Qatar) has no statutory
 * id ⇒ `null`; the field is hidden and a value is rejected.
 *
 * - `GST_INDIA`   — GSTIN, 15 chars, the CLAUDE.md regex (byte-identical).
 * - `VAT_UAE`     — TRN, 15 digits.
 * - `VAT_KSA`     — VAT registration number, 15 digits starting and ending
 *                    with 3 (ZATCA).
 * - `VAT_OMAN`    — VATIN, `OM` followed by 10 digits (12 chars).
 * - `VAT_BAHRAIN` — VAT account number, 15 digits.
 */
export const REGIME_TAX_ID_FORMAT: Readonly<Record<TaxRegime, TaxIdFormat | null>> = Object.freeze({
    GST_INDIA: {
        pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
        length: 15,
        example: '22AAAAA0000A1Z5',
        hint: '15-character GSTIN, e.g. 22AAAAA0000A1Z5',
    },
    VAT_UAE: {
        pattern: /^[0-9]{15}$/,
        length: 15,
        example: '100123456700003',
        hint: '15-digit Tax Registration Number (TRN)',
    },
    VAT_KSA: {
        pattern: /^3[0-9]{13}3$/,
        length: 15,
        example: '300000000000003',
        hint: '15-digit VAT registration number (TRN), starting and ending with 3',
    },
    VAT_OMAN: {
        pattern: /^OM[0-9]{10}$/,
        length: 12,
        example: 'OM1100123456',
        hint: 'VAT Identification Number (VATIN): OM followed by 10 digits, e.g. OM1100123456',
    },
    VAT_BAHRAIN: {
        pattern: /^[0-9]{15}$/,
        length: 15,
        example: '200000898300002',
        hint: '15-digit VAT account number',
    },
    NO_VAT: null,
});

/** Tax-id format for a regime, or `null` when the regime has no statutory id. */
export function getTaxIdFormat(regime: TaxRegime): TaxIdFormat | null {
    return REGIME_TAX_ID_FORMAT[regime];
}

/**
 * Derive the tax regime for a tenant currency. Pure — no I/O, total over the
 * in-scope {@link CurrencyCode} set. Nothing consumes this yet (task 240).
 */
export function getTaxRegime(currency: CurrencyCode): TaxRegime {
    return CURRENCY_TAX_REGIME[currency];
}

/**
 * The currency of a country the apps support as a tenant country (India and
 * the GCC), or `null` for any other country. The reverse of
 * {@link getCountryForCurrency}.
 */
export function getCurrencyForCountry(iso: string | null | undefined): CurrencyCode | null {
    const currencies = Object.keys(CURRENCY_COUNTRY) as CurrencyCode[];
    return currencies.find((c) => CURRENCY_COUNTRY[c].iso === iso) ?? null;
}

/** Tax regime of a supported country (India, GCC), or `null` for any other. */
export function getTaxRegimeForCountry(iso: string | null | undefined): TaxRegime | null {
    const currency = getCurrencyForCountry(iso);
    return currency ? CURRENCY_TAX_REGIME[currency] : null;
}

/**
 * Tax-id rule for a supplier registered outside India and the GCC, whose own
 * formats are not modelled: 5–30 upper-case letters, digits and inner hyphens
 * (e.g. GB123456789, DE123456789, an 18-character Chinese credit code).
 */
export const SUPPLIER_GENERIC_TAX_ID: TaxIdFormat = Object.freeze({
    pattern: /^[A-Z0-9][A-Z0-9-]{3,28}[A-Z0-9]$/,
    length: 30,
    example: '',
    hint: 'Tax or VAT registration number: letters, digits and hyphens',
});

/**
 * Tax-id format for a supplier registered in `iso` (client issue I-3): the
 * country's statutory format for India and the GCC, `null` where the country
 * has none (Kuwait, Qatar: no field, a value is refused), and
 * {@link SUPPLIER_GENERIC_TAX_ID} for any other country.
 */
export function supplierTaxIdFormat(iso: string): TaxIdFormat | null {
    const regime = getTaxRegimeForCountry(iso);
    return regime ? REGIME_TAX_ID_FORMAT[regime] : SUPPLIER_GENERIC_TAX_ID;
}

/**
 * Default per-product tax rate for a tenant currency — the value a product's
 * `gst_rate` column carries by default under that currency's regime.
 *
 * `products.gst_rate` is the existing per-product tax-rate column, reinterpreted
 * generically (NO migration, NO rename — see `docs/multi-currency-plan.md` §7 and
 * `docs/multi-currency-tasks/270-tax-slabs-regime-config.notes.md`):
 *
 * - **GST_INDIA** — returns the {@link GST_INDIA_PER_SLAB} sentinel: India tax is
 *   per GST slab (0/5/12/18/28 from the India-only `db/seeds/01_tax_slabs.sql`
 *   table), so there is NO single default rate. Callers gate on
 *   `regime === 'GST_INDIA'` and take the per-slab path. India behaviour is
 *   byte-identical — this never returns a usable percentage for INR.
 * - **GCC VAT regimes** — the country standard VAT rate, sourced ONLY from
 *   {@link REGIME_STANDARD_RATE} (UAE 5 / KSA 15 / Oman 5 / Bahrain 10), never
 *   from the India `tax_slabs` table.
 * - **NO_VAT** (Kuwait, Qatar) — `0`. Zero-rated / exempt products are also `0`.
 *
 * Pure config only — nothing consumes this yet (task 240 adds the gating).
 */
export function getStandardProductTaxRate(currency: CurrencyCode): number {
    return REGIME_STANDARD_RATE[getTaxRegime(currency)];
}

/**
 * Tax regime → the product tax rates (percentage points) a product may carry,
 * frozen. India sells on the GST slabs; a GCC VAT regime is zero-rated or its
 * standard rate ({@link REGIME_STANDARD_RATE}); `NO_VAT` is 0 only. The admin
 * and POS rate selects list these, and the API refuses any other rate for the
 * tenant (IS4-009).
 */
export const REGIME_PRODUCT_TAX_RATES: Readonly<Record<TaxRegime, readonly number[]>> = Object.freeze({
    GST_INDIA: Object.freeze([0, 5, 12, 18, 28]),
    VAT_UAE: Object.freeze([0, 5]),
    VAT_KSA: Object.freeze([0, 15]),
    VAT_OMAN: Object.freeze([0, 5]),
    VAT_BAHRAIN: Object.freeze([0, 10]),
    NO_VAT: Object.freeze([0]),
});

/** Every rate any regime allows — the DTO shape check; the tenant's regime narrows it. */
export const ALL_PRODUCT_TAX_RATES: readonly number[] = Object.freeze(
    [...new Set(Object.values(REGIME_PRODUCT_TAX_RATES).flat())].sort((a, b) => a - b),
);

/** The product tax rates a tenant of `currency` may use. */
export function getProductTaxRates(currency: CurrencyCode): readonly number[] {
    return REGIME_PRODUCT_TAX_RATES[getTaxRegime(currency)];
}
